import { useEffect, useState } from 'react';
import Select from 'react-select';
import { toast } from '../../../../../ui/toast';
import {
  ClipBoardIcons,
  MapPinIcons,
  SquareCheckIcons,
  TestNoteIcons,
} from '../../../../../icons/icons';
import { Button } from '../../../../../ui';
import { saveFilter } from '../../../../../utils/api_service/JobOpenings';
import { useGetPreQualConfigQuery } from '../../../../../utils/api_service/preQualApi';
import {
  PreQualCondition,
  PreQualFilter,
} from '../../../../../utils/types/JobOpenings';
import {
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface PreQualFilterDrawerContentProps {
  jobOpeningId: string;
  prequal_filter: PreQualFilter | null;
  setPrequalFilter: (filter: PreQualFilter | null) => void;
  onApply: () => void;
  searchObject: any;
}

export const PreQualFilterDrawerContent: React.FC<PreQualFilterDrawerContentProps> = ({
  jobOpeningId,
  prequal_filter,
  setPrequalFilter,
  onApply,
  searchObject,
}) => {
  const { data: config, isLoading } = useGetPreQualConfigQuery(jobOpeningId);
  const [localConditions, setLocalConditions] = useState<PreQualCondition[]>([]);

  // Initialize all questions as conditions
  useEffect(() => {
    if (config?.questions) {
      const allQuestions = config.questions;
      const initialConditions = allQuestions.map((q) => {
        // Find existing condition if it exists in prequal_filter
        const existing = prequal_filter?.conditions.find(
          (c) => c.question_key === q.questionKey,
        );
        if (existing) return existing;

        // Initialize empty condition based on type
        const empty: PreQualCondition = { question_key: q.questionKey || '' };
        if (q.questionType === 'multiple-choice') {
          empty.choice = { values: [] };
        } else if (q.questionType === 'number') {
          empty.number = { min: undefined, max: undefined };
        } else if (q.questionType === 'location') {
          empty.location = { country: '' };
        }
        return empty;
      });
      setLocalConditions(initialConditions);
    }
  }, [config, prequal_filter]);

  const questions = config?.questions || [];

  const updateCondition = (
    index: number,
    updates: Partial<PreQualCondition>,
  ) => {
    const updated = [...localConditions];
    updated[index] = { ...updated[index], ...updates };
    setLocalConditions(updated);
  };

  const handleApply = async () => {
    // Clean up conditions to remove undefined/null fields
    const cleanedConditions = localConditions
      .map((condition) => {
        const cleaned: any = { question_key: condition.question_key };

        if (condition.choice) {
          const values = condition.choice.values.filter((v) => v !== '');
          if (values.length > 0) {
            cleaned.choice = { values };
          }
        } else if (condition.number) {
          const num: any = {};
          if (condition.number.min !== undefined)
            num.min = condition.number.min;
          if (condition.number.max !== undefined)
            num.max = condition.number.max;
          if (Object.keys(num).length > 0) {
            cleaned.number = num;
          }
        } else if (condition.location) {
          const loc: any = {};
          // Radius mode: all three fields required
          if (
            condition.location.lat !== undefined &&
            condition.location.lng !== undefined &&
            condition.location.radius_km !== undefined
          ) {
            loc.lat = condition.location.lat;
            loc.lng = condition.location.lng;
            loc.radius_km = condition.location.radius_km;
          }
          if (condition.location.city) loc.city = condition.location.city;
          if (condition.location.country)
            loc.country = condition.location.country;

          if (Object.keys(loc).length > 0) {
            cleaned.location = loc;
          }
        }
        return cleaned;
      })
      .filter((c) => c.choice || c.number || c.location);

    const newFilter =
      cleanedConditions.length === 0 ? null : { conditions: cleanedConditions };

    setPrequalFilter(newFilter);

    try {
      // Also save to backend
      const savePayload = {
        ...searchObject,
        prequal_filter: newFilter,
      };
      await saveFilter(jobOpeningId, savePayload);
      toast.success('Filters applied and saved');
    } catch (error) {
      console.error('Error saving filter:', error);
      toast.error('Filters applied but failed to save to database');
    }

    onApply();
  };

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'multiple-choice':
        return <SquareCheckIcons size={16} color="#10b981" />;
      case 'number':
        return <ClipBoardIcons size={16} color="#3b82f6" />;
      case 'location':
        return <MapPinIcons size={16} color="#f43f5e" />;
      default:
        return <TestNoteIcons size={16} color="#64748b" />;
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 256,
        }}
      >
        <CircularProgress size={32} sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'rgba(248, 250, 252, 0.3)',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
              Questionnaire Filters
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                bgcolor: '#f1f5f9',
                color: '#64748b',
                px: 1,
                py: 0.25,
                borderRadius: '10px',
                fontWeight: 500,
              }}
            >
              {questions.length} Questions Available
            </Typography>
          </Stack>

          <Stack spacing={4}>
            {localConditions.map((condition, index) => {
              const question = questions.find(
                (q) => q.questionKey === condition.question_key,
              );
              if (!question) return null;

              return (
                <Box key={condition.question_key} sx={{ position: 'relative' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        p: 0.75,
                        bgcolor: '#f8fafc',
                        borderRadius: '8px',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {getQuestionIcon(question.questionType)}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#334155',
                        lineHeight: 1.4,
                      }}
                    >
                      {question.question}
                    </Typography>
                  </Stack>

                  <Box sx={{ pl: 4.5 }}>
                    {/* Choice Filter */}
                    {question.questionType === 'multiple-choice' && (
                      <Box>
                        <Select
                          isMulti
                          placeholder="Select allowed answers..."
                          options={question.choices?.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                          value={condition.choice?.values.map((v) => ({
                            value: v,
                            label: v,
                          }))}
                          onChange={(options: any) =>
                            updateCondition(index, {
                              choice: {
                                values: (options || []).map(
                                  (o: any) => o.value,
                                ),
                              },
                            })
                          }
                          styles={{
                            control: (base: any) => ({
                              ...base,
                              borderRadius: '10px',
                              borderColor: '#e2e8f0',
                              fontSize: '13px',
                              '&:hover': { borderColor: '#cbd5e1' },
                            }),
                          }}
                        />
                      </Box>
                    )}

                    {/* Number Filter */}
                    {question.questionType === 'number' && (
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 700,
                              color: '#94a3b8',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              mb: 0.75,
                            }}
                          >
                            Min Value
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={condition.number?.min ?? ''}
                            onChange={(e) =>
                              updateCondition(index, {
                                number: {
                                  ...condition.number,
                                  min: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                },
                              })
                            }
                            placeholder="Any"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '13px',
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 700,
                              color: '#94a3b8',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              mb: 0.75,
                            }}
                          >
                            Max Value
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            value={condition.number?.max ?? ''}
                            onChange={(e) =>
                              updateCondition(index, {
                                number: {
                                  ...condition.number,
                                  max: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                },
                              })
                            }
                            placeholder="Any"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '13px',
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    )}

                    {/* Location Filter */}
                    {question.questionType === 'location' && (
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '9px',
                                fontWeight: 700,
                                color: '#94a3b8',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                mb: 0.75,
                              }}
                            >
                              Country
                            </Typography>
                            <TextField
                              type="text"
                              size="small"
                              fullWidth
                              value={condition.location?.country || ''}
                              onChange={(e) =>
                                updateCondition(index, {
                                  location: {
                                    ...condition.location,
                                    country: e.target.value,
                                  },
                                })
                              }
                              placeholder="e.g. Australia"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '9px',
                                fontWeight: 700,
                                color: '#94a3b8',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                mb: 0.75,
                              }}
                            >
                              City (Optional)
                            </Typography>
                            <TextField
                              type="text"
                              size="small"
                              fullWidth
                              value={condition.location?.city || ''}
                              onChange={(e) =>
                                updateCondition(index, {
                                  location: {
                                    ...condition.location,
                                    city: e.target.value,
                                  },
                                })
                              }
                              placeholder="e.g. Sydney"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                },
                              }}
                            />
                          </Box>
                        </Stack>

                        <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'grey.100' }}>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 700,
                              color: '#94a3b8',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              mb: 1,
                            }}
                          >
                            OR Radius Mode
                          </Typography>
                          <Stack direction="row" spacing={1.5}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '8px', color: '#94a3b8', mb: 0.5 }}>
                                Lat
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={condition.location?.lat ?? ''}
                                onChange={(e) =>
                                  updateCondition(index, {
                                    location: {
                                      ...condition.location,
                                      lat: e.target.value
                                        ? Number(e.target.value)
                                        : undefined,
                                    },
                                  })
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    px: 1,
                                    py: 0.75,
                                  },
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '8px', color: '#94a3b8', mb: 0.5 }}>
                                Lng
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={condition.location?.lng ?? ''}
                                onChange={(e) =>
                                  updateCondition(index, {
                                    location: {
                                      ...condition.location,
                                      lng: e.target.value
                                        ? Number(e.target.value)
                                        : undefined,
                                    },
                                  })
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    px: 1,
                                    py: 0.75,
                                  },
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '8px', color: '#94a3b8', mb: 0.5 }}>
                                Radius (km)
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={condition.location?.radius_km ?? ''}
                                onChange={(e) =>
                                  updateCondition(index, {
                                    location: {
                                      ...condition.location,
                                      radius_km: e.target.value
                                        ? Number(e.target.value)
                                        : undefined,
                                    },
                                  })
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    px: 1,
                                    py: 0.75,
                                  },
                                }}
                              />
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    )}

                    {question.questionType === 'text' && (
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: '#fffbeb',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: '#fef3c7',
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#fbbf24',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '10px',
                            color: '#b45309',
                            fontStyle: 'italic',
                          }}
                        >
                          Text matching is not supported in the current pre-qual
                          filter spec.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          p: 3,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Typography
          component="button"
          onClick={() => {
            const reset = localConditions.map((c) => {
              const r = { ...c };
              if (r.choice) r.choice = { values: [] };
              if (r.number) r.number = { min: undefined, max: undefined };
              if (r.location) r.location = { country: '', city: '' };
              return r;
            });
            setLocalConditions(reset);
            setPrequalFilter(null);
          }}
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#64748b',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            p: 0,
            transition: 'color 0.2s',
            '&:hover': { color: '#334155' },
          }}
        >
          Reset All
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button label="Cancel" onClick={onApply} variant="secondary" />
          <Button
            label="Apply Filters"
            onClick={handleApply}
            variant="primary"
          />
        </Stack>
      </Stack>
    </Box>
  );
};
